import { FC, ReactNode, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { CurrencyField } from '@/components/common/CurrencyField'
import {
  GRUPO_TOPO,
  ItemFormulario,
  MARCO_EXIGENCIA_LABEL,
  PendenciaFormulario,
  RespostaFormulario,
  SchemaFormulario,
  ValorResposta,
} from '@/types/formulario'
import {
  comResposta,
  indexarRespostas,
  itemObrigatorio,
  itemVisivel,
  quantidadeItensGrupo,
  resolvedor,
  semItemDoGrupo,
  TETO_ITENS_GRUPO,
  valorComoTexto,
  valorResposta,
} from '@/utils/formularioCondicao'
import { formatCNPJ, formatCPF } from '@/utils/formatters'

export interface FormularioDinamicoProps {
  schema: SchemaFormulario
  respostas: RespostaFormulario[]
  /** Pendências calculadas pelo SERVIDOR (última resposta do GET/PUT). */
  pendencias?: PendenciaFormulario[]
  somenteLeitura?: boolean
  onChange: (respostas: RespostaFormulario[]) => void
}

/** Rótulo do item de um grupo: "Sócio 1", "Sócio 2"… */
const rotuloItem = (grupo: ItemFormulario, i: number) =>
  grupo.repeatableGroup || grupo.quantidadeDe ? `${grupo.rotulo} ${i + 1}` : grupo.rotulo

/**
 * A pendência é desta posição? Campo do topo: grupo "" (índice ignorado). Item de grupo:
 * mesmo índice. Grupo inteiro (MIN_ITENS, QUANTIDADE_ITENS, SOMA): `indice` e `campo` nulos.
 */
const mesmaPosicao = (p: PendenciaFormulario, grupo: string, indice: number | null, campo: string | null) => {
  if ((p.grupo ?? GRUPO_TOPO) !== grupo || (p.campo ?? null) !== campo) return false
  if (grupo === GRUPO_TOPO) return true
  return indice == null ? p.indice == null : p.indice === indice
}

/**
 * Renderizador CONTROLADO do formulário dinâmico do produto (AN-6 §7).
 *
 * Tudo vem do schema versionado (dado do ADMIN) — nenhum campo fixo. `condition` e
 * `requiredWhen` são avaliados aqui SÓ para mostrar/ocultar e pôr o asterisco; quem diz o
 * que falta é o servidor, pelas `pendencias`, que aparecem junto do campo/grupo.
 * Grupos (um nível) viram cartões: com `quantidadeDe` o número de cartões segue o NUMERO
 * indicado (até `maxItems`); sem ele, o grupo repetível tem "Adicionar"/"Remover".
 */
export const FormularioDinamico: FC<FormularioDinamicoProps> = ({
  schema,
  respostas,
  pendencias = [],
  somenteLeitura = false,
  onChange,
}) => {
  const indice = useMemo(() => indexarRespostas(respostas), [respostas])
  /** Cartões vazios acrescentados em grupo livre (ainda sem resposta). */
  const [extras, setExtras] = useState<Record<string, number>>({})

  const alterar = (grupo: string, posicao: number, campo: string, valor: ValorResposta) =>
    onChange(comResposta(respostas, grupo, posicao, campo, valor))

  const pendenciasDe = (grupo: string, posicao: number | null, campo: string | null) =>
    pendencias.filter((p) => mesmaPosicao(p, grupo, posicao, campo))

  const renderCampo = (
    item: ItemFormulario,
    grupo: string,
    posicao: number,
    resolver: (chave: string) => ValorResposta | undefined
  ): ReactNode => {
    if (!itemVisivel(item, resolver)) return null
    const obrigatorio = itemObrigatorio(item, resolver)
    const valor = valorResposta(indice, grupo, posicao, item.chave)
    const erros = pendenciasDe(grupo, posicao, item.chave)
    const erro = erros.length > 0
    const ajuda =
      erros.map((p) => p.mensagem).join(' ') ||
      item.ajuda ||
      (obrigatorio && item.obrigatorioEm === 'ENCAMINHAMENTO_PARCEIRO'
        ? `Exigido no ${MARCO_EXIGENCIA_LABEL.ENCAMINHAMENTO_PARCEIRO}.`
        : undefined)
    const id = `form-${grupo || 'topo'}-${posicao}-${item.chave}`
    const set = (v: ValorResposta) => alterar(grupo, posicao, item.chave, v)
    const texto = valorComoTexto(valor) ?? ''
    const comum = {
      id,
      label: item.rotulo,
      required: obrigatorio,
      error: erro,
      helperText: ajuda,
      fullWidth: true,
      disabled: somenteLeitura,
      size: 'small' as const,
    }

    switch (item.tipo) {
      case 'SIM_NAO':
        return (
          <FormControl key={id} error={erro} disabled={somenteLeitura} required={obrigatorio}>
            <FormLabel id={`${id}-rotulo`}>{item.rotulo}</FormLabel>
            <RadioGroup
              row
              aria-labelledby={`${id}-rotulo`}
              value={texto}
              onChange={(e) => set(e.target.value === 'true')}
            >
              <FormControlLabel value="true" control={<Radio size="small" />} label="Sim" />
              <FormControlLabel value="false" control={<Radio size="small" />} label="Não" />
            </RadioGroup>
            {ajuda && <FormHelperText>{ajuda}</FormHelperText>}
          </FormControl>
        )
      case 'OPCAO':
        return (
          <TextField key={id} {...comum} select value={texto} onChange={(e) => set(e.target.value || null)}>
            <MenuItem value="">
              <em>Selecione</em>
            </MenuItem>
            {(item.opcoes ?? []).map((o) => (
              <MenuItem key={o.valor} value={o.valor}>
                {o.rotulo}
              </MenuItem>
            ))}
          </TextField>
        )
      case 'MOEDA':
        return (
          <CurrencyField
            key={id}
            {...comum}
            value={typeof valor === 'number' ? valor : Number(texto) || 0}
            // R$ 0,00 é o campo "apagado" do CurrencyField — vira sem resposta.
            onChange={(n) => set(n > 0 ? n : null)}
          />
        )
      case 'NUMERO':
      case 'PERCENTUAL':
        return (
          <TextField
            key={id}
            {...comum}
            type="number"
            value={texto}
            inputProps={{
              min: item.tipo === 'PERCENTUAL' ? item.min ?? 0 : item.min ?? undefined,
              max: item.tipo === 'PERCENTUAL' ? item.max ?? 100 : item.max ?? undefined,
              step: 'any',
            }}
            InputProps={item.tipo === 'PERCENTUAL' ? { endAdornment: <Typography variant="body2">%</Typography> } : undefined}
            onChange={(e) => {
              const t = e.target.value
              const n = Number(t)
              set(t.trim() === '' || !Number.isFinite(n) ? null : n)
            }}
          />
        )
      case 'DATA':
        return (
          <TextField
            key={id}
            {...comum}
            type="date"
            value={texto}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => set(e.target.value || null)}
          />
        )
      case 'CPF':
        return (
          <TextField
            key={id}
            {...comum}
            value={formatCPF(texto) || texto}
            inputProps={{ inputMode: 'numeric' }}
            onChange={(e) => set(e.target.value.replace(/\D/g, '').slice(0, 11) || null)}
          />
        )
      case 'CNPJ':
        return (
          <TextField
            key={id}
            {...comum}
            value={texto.length === 14 ? formatCNPJ(texto) : texto}
            // CNPJ alfanumérico (Receita, 2026): letras e números.
            inputProps={{ autoCapitalize: 'characters', spellCheck: false }}
            onChange={(e) =>
              set(e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 14) || null)
            }
          />
        )
      case 'TEXTO_LONGO':
        return (
          <TextField key={id} {...comum} multiline minRows={3} value={texto} onChange={(e) => set(e.target.value)} />
        )
      case 'EMAIL':
        return <TextField key={id} {...comum} type="email" value={texto} onChange={(e) => set(e.target.value)} />
      case 'TELEFONE':
        return (
          <TextField
            key={id}
            {...comum}
            type="tel"
            value={texto}
            inputProps={{ inputMode: 'tel' }}
            onChange={(e) => set(e.target.value)}
          />
        )
      case 'TEXTO':
      default:
        return <TextField key={id} {...comum} value={texto} onChange={(e) => set(e.target.value)} />
    }
  }

  const renderGrupo = (grupo: ItemFormulario): ReactNode => {
    const topo = resolvedor(indice)
    if (!itemVisivel(grupo, topo)) return null
    const base = quantidadeItensGrupo(grupo, respostas)
    const teto = grupo.maxItems ?? TETO_ITENS_GRUPO
    const livre = !!grupo.repeatableGroup && !grupo.quantidadeDe
    const n = livre ? Math.min(Math.max(base, extras[grupo.chave] ?? 0), teto) : base
    const doGrupo = pendenciasDe(grupo.chave, null, null)
    const pedidoAcimaDoTeto =
      !!grupo.quantidadeDe &&
      Number(valorComoTexto(valorResposta(indice, GRUPO_TOPO, 0, grupo.quantidadeDe))) > teto

    return (
      <Box key={`grupo-${grupo.chave}`} component="section" aria-label={grupo.rotulo}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          {grupo.rotulo}
          {itemObrigatorio(grupo, topo) ? ' *' : ''}
        </Typography>
        {grupo.ajuda && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {grupo.ajuda}
          </Typography>
        )}
        {pedidoAcimaDoTeto && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Este formulário aceita até {teto} {grupo.rotulo.toLowerCase()}(s).
          </Alert>
        )}
        <Stack spacing={1.5}>
          {Array.from({ length: n }, (_, i) => {
            const irmao = resolvedor(indice, grupo, i)
            return (
              <Paper
                key={i}
                variant="outlined"
                sx={{ p: 2 }}
                role="group"
                aria-label={rotuloItem(grupo, i)}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {rotuloItem(grupo, i)}
                  </Typography>
                  {livre && !somenteLeitura && n > (grupo.minItems ?? 1) && (
                    <Button
                      size="small"
                      color="inherit"
                      startIcon={<DeleteOutlineIcon fontSize="small" />}
                      onClick={() => {
                        setExtras((prev) => ({ ...prev, [grupo.chave]: Math.max(n - 1, 0) }))
                        onChange(semItemDoGrupo(respostas, grupo.chave, i))
                      }}
                    >
                      Remover
                    </Button>
                  )}
                </Stack>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    gap: 2,
                  }}
                >
                  {(grupo.campos ?? []).map((c) => renderCampo(c, grupo.chave, i, irmao))}
                </Box>
                {pendenciasDe(grupo.chave, i, null).map((p, k) => (
                  <FormHelperText key={k} error>
                    {p.mensagem}
                  </FormHelperText>
                ))}
              </Paper>
            )
          })}
        </Stack>
        {livre && !somenteLeitura && n < teto && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            sx={{ mt: 1 }}
            onClick={() => setExtras((prev) => ({ ...prev, [grupo.chave]: n + 1 }))}
          >
            Adicionar {grupo.rotulo.toLowerCase()}
          </Button>
        )}
        {doGrupo.map((p, k) => (
          <Alert key={k} severity="error" sx={{ mt: 1 }}>
            {p.mensagem}
          </Alert>
        ))}
      </Box>
    )
  }

  const topo = resolvedor(indice)
  return (
    <Stack spacing={2.5}>
      {(schema.itens ?? []).map((item) =>
        item.tipo === 'GRUPO' ? renderGrupo(item) : renderCampo(item, GRUPO_TOPO, 0, topo)
      )}
    </Stack>
  )
}

export default FormularioDinamico
